const number = new Intl.NumberFormat('en-GB', { maximumFractionDigits: 0 });
const decimal = new Intl.NumberFormat('en-GB', { maximumFractionDigits: 2 });

export const formatNumber = (value: number) => number.format(value);
export const formatDecimal = (value: number) => decimal.format(value);

export const formatPercent = (value: number) =>
  `${value > 0 ? '+' : ''}${decimal.format(value)}%`;

export const formatDate = (iso: string) => {
  const d = new Date(iso.length === 10 ? `${iso}T00:00:00Z` : iso);
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

export const formatShortDate = (iso: string) => {
  const d = new Date(iso.length === 10 ? `${iso}T00:00:00Z` : iso);
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
};

/**
 * Date and time to the MINUTE, for "when did this last change".
 *
 * A bare date cannot answer that question on the day it matters: a topic
 * claimed, released and re-claimed within an hour reads as three identical
 * "12 Aug 2026" rows.
 */
export const formatDateTime = (iso: string) => {
  const d = new Date(iso.length === 10 ? `${iso}T00:00:00Z` : iso);
  return d.toLocaleString('en-GB', {
    day: 'numeric', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: false,
  });
};

/**
 * Date and time to the SECOND, for the Activity log.
 *
 * Diagnosing a pipeline run means lining an App row up against a GitHub
 * Actions timestamp, and six runs of the same kind on one day are
 * indistinguishable without it. Rendered in the reader's own timezone, which
 * is NOT what the Actions log shows - so every row also carries its exact UTC
 * instant in `title`, because an off-by-one-hour comparison is worse than no
 * comparison.
 */
export const formatTimestamp = (iso: string) => {
  const d = new Date(iso.length === 10 ? `${iso}T00:00:00Z` : iso);
  return d.toLocaleString('en-GB', {
    day: 'numeric', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
  });
};

/**
 * How long a run took. The number that says whether a failure was instant
 * (refused before it started) or slow (a model that ran out of budget).
 */
export const formatDuration = (fromIso: string, toIso: string) => {
  const ms = new Date(toIso).getTime() - new Date(fromIso).getTime();
  if (!Number.isFinite(ms) || ms < 0) return '';
  const seconds = Math.round(ms / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  return `${minutes}m ${String(seconds % 60).padStart(2, '0')}s`;
};

/** 'unique_visitors' -> 'Unique visitors'. Metric names are an open set. */
export const metricLabel = (metric: string) => {
  const label = metric.replaceAll('_', ' ');
  const special: Record<string, string> = {
    'uptime pct': 'Uptime %',
    'page load ms': 'Page load (ms)',
    'bounce pct': 'Single-page visits %',
    // Named for what it is rather than "bots": the number is here so a client
    // can see WHY the human figures stepped down, not as a scoreboard.
    'bot hits': 'Automated requests filtered',
    revenue: 'Revenue',
    // The waitlist's daily count (the Website Manager writes it): an outcome,
    // so it sorts with leads rather than after the traffic.
    signups: 'Sign-ups',
  };
  const mapped = special[label] ?? label;
  return mapped.charAt(0).toUpperCase() + mapped.slice(1);
};

/** Rates and durations read as averages; counts read as sums. */
export const isRateMetric = (metric: string) =>
  metric.endsWith('_pct') || metric.endsWith('_rate') || metric.endsWith('_avg')
  || metric.endsWith('_ms');

/**
 * Headline order for KPI tiles: the traffic story first, then outcomes,
 * then quality. Unknown metrics sort after, alphabetically.
 */
const KPI_ORDER = ['visits', 'unique_visitors', 'pageviews', 'bounce_pct', 'leads', 'signups',
  'uptime_pct', 'page_load_ms', 'bot_hits'];
export const kpiRank = (metric: string) => {
  const index = KPI_ORDER.indexOf(metric);
  return index === -1 ? KPI_ORDER.length : index;
};

export const STATUS_LABELS: Record<string, string> = {
  live: 'Live',
  in_development: 'In development',
  paused: 'Paused',
  retired: 'Retired',
};

export const CATEGORY_LABELS: Record<string, string> = {
  update: 'Update',
  report: 'Report',
  guide: 'Guide',
  info: 'Info',
};

/*
 * Portuguese conventions, for the Finance module only (2026-10-12).
 *
 * A ledger is read against invoices, bank statements and the accountant's
 * figures, and in Portugal all three write "1 500,00 €" and "1 out. 2026".
 * The browser test found "€-50.00" and "1 Oct 2026" beside invoices that
 * said otherwise. The rest of the App stays en-GB until somebody decides it
 * should not: these are separate functions so that decision is one import.
 *
 * The date is assembled rather than asked of Intl: pt-PT's own short date is
 * "1/10/2026", which an English page makes ambiguous with the American order.
 */
const eurosPt = new Intl.NumberFormat('pt-PT', { style: 'currency', currency: 'EUR' });
const monthPt = new Intl.DateTimeFormat('pt-PT', { month: 'short', timeZone: 'UTC' });
const monthPtLocal = new Intl.DateTimeFormat('pt-PT', { month: 'short' });

/** Cents as "1500,00 €" or "-50,00 €". */
export const formatEurosPt = (cents: number) => eurosPt.format(cents / 100);

/** "1 out. 2026", for a calendar day: no timezone can move it. */
export const formatDatePt = (iso: string) => {
  const d = new Date(`${iso.slice(0, 10)}T00:00:00Z`);
  return `${d.getUTCDate()} ${monthPt.format(d)} ${d.getUTCFullYear()}`;
};

/** "12 out. 2026, 14:05", in the reader's own timezone. */
export const formatDateTimePt = (iso: string) => {
  const d = new Date(iso);
  const time = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  return `${d.getDate()} ${monthPtLocal.format(d)} ${d.getFullYear()}, ${time}`;
};
